import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Badge, Card } from '@/admin-design/_common'

interface ContainerEntry {
  Name: string
  IPv4Address: string
}

interface Network {
  id: string
  name: string
  driver: string
  scope: string
  containers: Record<string, ContainerEntry> | null
}


export default function Networks() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['networks'],
    queryFn: () => api.listNetworks(),
    refetchInterval: 30000,
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-foreground">Docker Networks</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Network topology and container connections per node</p>
      </div>

      {error && (
        <div className="bg-destructive-soft text-destructive-soft-foreground px-4 py-3 rounded border border-destructive-soft text-sm">
          {(error as Error).message}
        </div>
      )}

      {isLoading && (
        <div className="space-y-4 animate-pulse">
          {[1, 2].map(i => <div key={i} className="h-40 bg-muted rounded-lg" />)}
        </div>
      )}

      {data?.map((node) => (
        <div key={node.node_name} className="space-y-3">
          <h2 className="font-semibold text-sm text-foreground flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${node.networks ? 'bg-success' : 'bg-muted-foreground/40'}`} />
            {node.node_name}
          </h2>

          {!node.networks && (
            <div className="text-xs text-muted-foreground pl-4">Node offline — cannot fetch networks</div>
          )}

          {(node.networks as Network[] | null)?.map((net) => {
            const containerCount = Object.keys(net.containers || {}).length
            return (
              <Card key={net.id} className="overflow-hidden">
                <div className="px-4 py-2.5 bg-muted/50 border-b border-border flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-medium text-sm text-card-foreground break-all min-w-0">
                    {net.name}
                  </span>
                  <Badge className="rounded shrink-0">{net.driver}</Badge>
                  <span className="text-xs text-muted-foreground shrink-0">{net.scope}</span>
                  <span className="text-xs text-muted-foreground sm:ml-auto shrink-0">{containerCount} containers</span>
                </div>
                {containerCount > 0 ? (
                  <div className="divide-y divide-border">
                    {Object.entries(net.containers ?? {}).map(([cid, entry]) => {
                      const c = entry as ContainerEntry
                      return (
                      <div
                        key={cid}
                        className="px-4 py-2 text-xs flex flex-col sm:flex-row sm:items-center sm:gap-3"
                      >
                        <span className="font-medium text-card-foreground sm:w-48 sm:truncate break-all">
                          {c.Name}
                        </span>
                        <div className="flex items-center gap-3 text-muted-foreground mt-0.5 sm:mt-0 sm:flex-1">
                          <span className="font-mono">{c.IPv4Address || '-'}</span>
                          <span className="font-mono ml-auto truncate" title={cid}>
                            {cid}
                          </span>
                        </div>
                      </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="px-4 py-3 text-xs text-muted-foreground">No containers attached</div>
                )}
              </Card>
            )
          })}
        </div>
      ))}
    </div>
  )
}
